import React, { useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Paper, Stack, IconButton, Skeleton } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

import { useGetScanQuery } from 'api/scan/queries/useGetScanQuery';
import PointCloudViewer from 'containers/ComparePointCloudVirtualTour/components/pointcloud/PointCloudContainer';
import { UnityViewerContextProvider } from 'contexts/UnityViewerContext';
import { UnityViewer } from 'components/UnityViewer';
import PCLDViewerHeader from 'containers/PointCloudViewer/components/PointCloudViewerHeader';
import { useGetOutputPointcloud } from 'api/output/queries/useGetOutputPointcloud';
import FullScreenInit from 'components/FullScreenInit';
import { useLoading } from 'contexts/LoadingContext';
import Toolbar from 'components/ViewerWrapper/Toolbar';
import { toast, Bounce } from 'react-toastify';

const ComparePointCloudVirtualTour = () => {
  const { ptcldOutputId2: outputId, fullscreen } = useLoading();

  const {
    data: pointCloudData,
    isLoading: dataLoading,
    error: dataError,
  } = useGetOutputPointcloud(outputId);

  const containerRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const onMouseMove = (moveEvent: MouseEvent) => {
      // Cancel any existing animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      // Use requestAnimationFrame for smoother rendering
      animationFrameRef.current = requestAnimationFrame(() => {
        const containerRect = container.getBoundingClientRect();
        const x = moveEvent.clientX - containerRect.left;
        const containerWidth = containerRect.width;

        // Calculate percentage, constrain between 10% and 90%
        const percentage = Math.max(10, Math.min(90, (x / containerWidth) * 100));
        
        // Direct width manipulation via CSS
        const leftPanel = container.querySelector('.left-panel') as HTMLDivElement;
        const rightPanel = container.querySelector('.right-panel') as HTMLDivElement;
        
        if (leftPanel && rightPanel) {
          leftPanel.style.width = `${percentage}%`;
          rightPanel.style.width = `${100 - percentage}%`;
        }
      });
    };

    const onMouseUp = () => {
      // Cancel any remaining animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };
  
    if (dataError) {
      return toast.error( dataError?.message, {
        position: "bottom-left",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
        transition: Bounce,
      });
    }

  return (
    <Stack height="100%" sx={{ flexDirection: "column" }}>
      {/* <Stack>
        <PCLDViewerHeader viewerName="Point Cloud - Virtual Tour Viewer" />
      </Stack> */}

      <Stack
        ref={containerRef}
        sx={{
          height: "100%",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Stack
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "row",
          }}
        >
          <Stack
            className="left-panel"
            sx={{
              height: "100%",
              width: "50%",
              transition: "width 0.2s ease-out",
              overflow: "hidden",
            }}
          >
            <UnityViewerContextProvider>
              <UnityViewer scanType="uniscan-compare" isEnabled={false} />
            </UnityViewerContextProvider>
          </Stack>

          <Stack
            sx={{
              width: "20px",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              cursor: "col-resize",
              zIndex: 1000,
              backgroundColor: "transparent",
            }}
            onMouseDown={handleMouseDown}
          >
            <IconButton
              size="small"
              sx={{
                backgroundColor: "grey.300",
                "&:hover": { backgroundColor: "grey.400" },
              }}
            >
              <DragIndicatorIcon />
            </IconButton>
          </Stack>

          <Stack
            className="right-panel"
            sx={{
              height: "100%",
              width: "50%",
              transition: "width 0.2s ease-out",
              overflow: "hidden",
            }}
          >
            <PointCloudViewer
              pointCloudData={pointCloudData}
              dataError={dataError}
            />
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default ComparePointCloudVirtualTour;